import _ from "lodash";
import client from "lib/apolloClient/client";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  createWeeklyPlannerTemplateMutation,
  copyWeeklyPlannerTemplateMutation,
  deleteWeeklyPlannerTemplateMutation,
  updateWeeklyPlannerTemplateMutation,
  createWeeklyPlannerSectionMutation,
  updateWeeklyPlannerSectionMutation,
  deleteWeeklyPlannerSectionMutation,
  moveWeeklyPlannerSectionContentMutation,
  reorderWeeklyPlannerSectionContentMutation,
  clearWeeklyPlannerSectionContentMutation,
  duplicateWeeklyPlannerSectionContentMutation,
  getWeeklyPlannerDownloadUrlMutation,
  importResourcesToSectionMutation,
  updateWeeklyPlannerSectionItemsMutation,
  updateWeeklyPlannerSectionDescriptionMutation,
} from "./ClassPlannersMutations";
import asyncWrap from "Utils/asyncWrap";
import {
  getWeeklyPlannerTemplateByCourseQuery,
  getWeeklyPlannerTemplateSectionsQuery,
} from "./ClassPlannersQueries";
import {
  getWeeklyPlannerFragmentFromCache,
  getWeeklyPlannerTemplateFromCache,
  writeWeeklyPlannerTemplatesToCache,
  readWeeklyPlannerTemplateSectionFragment,
  writeWeeklyPlannerTemplateSectionFragmentToCache,
} from "./ClassPlannersGraphqlHelpers";
import { generateRandomId } from "Utils";
import update from "immutability-helper";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";

import {
  getSelectedAcademicYearId,
  getSelectedCourseId,
} from "modules/CommonSelectors";
import {
  goToRelativeRoute,
  downloadFromUrl,
  setProgress,
  removeProgressElement,
} from "modules/Services";
import moment from "moment";

export const NAME = "classPlanner";

export const UPDATE_CONFIGURATION_FILTER =
  "UPDATE_CONFIGURATION_FILTER" + " " + NAME;
export const INITIALIZE_CONFIGURATION_FILTER =
  "INITIALIZE_CONFIGURATION_FILTER" + " " + NAME;
export const SET_ASSIGNMENT_MODAL_DATA =
  "SET_ASSIGNMENT_MODAL_DATA" + " " + NAME;

export const updateConfigurationFilter = data => {
  return { type: UPDATE_CONFIGURATION_FILTER, data };
};

export const initializeConfigurationFilter = () => {
  return { type: INITIALIZE_CONFIGURATION_FILTER };
};

export const setAssignmentModalData = data => {
  return { type: SET_ASSIGNMENT_MODAL_DATA, data };
};

export const createWeeklyPlannerTemplate = ({
  title,
  startDate,
  endDate,
  defaultAccessState,
  courses,
  addedExceptionalCollaborators,
}) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const refetchQueries = [
      {
        query: getWeeklyPlannerTemplateByCourseQuery,
        variables: {
          id: courseId,
        },
      },
    ];

    let res;
    try {
      res = await client.mutate({
        mutation: createWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            title,
            startDate,
            endDate,
            defaultAccessState,
            courses,
            addedExceptionalCollaborators,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      const plannerId = _.get(
        res,
        "data.planner.createWeeklyPlannerTemplate.id"
      );
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_created_successfully",
          type: "success",
        })
      );
      if (plannerId)
        dispatch(
          goToRelativeRoute({
            route: `${plannerId}/weekly`,
            type: "push",
          })
        );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};
export const copyWeeklyPlannerTemplate = ({
  id,
  title,
  startDate,
  endDate,
  itemTypes,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const academicYearId = getSelectedAcademicYearId(getState());
    const courseGradeFilters = {
      academicYearId,
    };
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );
    const courseDetails = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });
    const courseTitle = _.get(courseDetails, "title", "");
    const primaryGrade = _.get(courseDetails, "primaryGrade.id");

    const {
      id: userId,
      first_name = "",
      last_name = "",
      userEntityType = "Staff",
    } = userInfo;

    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };
    const data = getWeeklyPlannerTemplateFromCache({
      id: courseId,
      exceptionalCollaboratorFilters,
    });

    const newClassPlannerTemplate = {
      id: generateRandomId(),
      title,
      startDate,
      endDate,
      createdAt: new Date().toISOString(),
      defaultAccessState: "VIEW",
      collaborators: {
        edges: [],
        __typename: "UnitPlanCollaboratorConnection",
      },
      createdBy: {
        id: userId,
        firstName: first_name,
        lastName: last_name,
        __typename: userEntityType,
      },
      courses: [
        {
          id: courseId,
          title: courseTitle,
          primaryGrade: {
            id: primaryGrade,
            __typename: "Grade",
          },
          __typename: "Course",
        },
      ],
      updatedAt: null,
      updatedBy: null,
      isSharedWithFamilies: false,
      isSharedWithStudents: false,
      exceptionalCollaborators: null,
      enabledSections: null,
      __typename: "WeeklyPlannerTemplate",
    };

    try {
      await client.mutate({
        mutation: copyWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            title,
            startDate,
            endDate,
            itemTypes,
            courses: [courseId],
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            copyWeeklyPlannerTemplate: newClassPlannerTemplate,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { copyWeeklyPlannerTemplate },
            },
          }
        ) => {
          const updatedData = update(data, {
            weeklyPlannerTemplates: {
              $unshift: [copyWeeklyPlannerTemplate],
            },
          });
          setTimeout(() =>
            writeWeeklyPlannerTemplatesToCache({
              variables: { id: courseId },
              data: { node: updatedData },
            })
          );
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:duplicate_success_message",
          type: "success",
        })
      );
    } catch (e) {
      writeWeeklyPlannerTemplatesToCache({
        variables: { id: courseId, exceptionalCollaboratorFilters },
        data: { node: data },
      });

      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};
export const updateWeeklyPlannerTemplate = ({
  id,
  title,
  startDate,
  endDate,
}) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const refetchQueries = [
      {
        query: getWeeklyPlannerTemplateByCourseQuery,
        variables: {
          id: courseId,
        },
      },
    ];

    try {
      await client.mutate({
        mutation: updateWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            title,
            startDate,
            endDate,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_updated",
          type: "success",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const deleteWeeklyPlannerTemplate = ({ id }) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const data = getWeeklyPlannerTemplateFromCache({
      id: courseId,
    });

    const updatedData = update(data, {
      weeklyPlannerTemplates: {
        $set: _.filter(data.weeklyPlannerTemplates, item => item.id !== id),
      },
    });

    writeWeeklyPlannerTemplatesToCache({
      variables: { id: courseId },
      data: { node: updatedData },
    });

    try {
      await client.mutate({
        mutation: deleteWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
          },
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_deleted",
          type: "success",
        })
      );
    } catch (e) {
      writeWeeklyPlannerTemplatesToCache({
        variables: { id: courseId },
        data: { node: data },
      });
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const shareWeeklyPlannerTemplate = ({
  id,
  courses: allCourses = [],
  addedExceptionalCollaborators = [],
  removedExceptionalCollaborators = [],
  defaultAccessState,
  enabledSections,
  isSharedWithFamilies,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const getPlannerFragment = getWeeklyPlannerFragmentFromCache({
      id,
      variables: {},
    });

    const prevCourses = _.map(_.get(getPlannerFragment, "courses", []), "id");
    const added = _.difference(allCourses, prevCourses);
    const removed = _.difference(prevCourses, allCourses);
    const courses = { added, removed };

    const existingExceptionalCollaborators = _.get(
      getPlannerFragment,
      "exceptionalCollaborators.edges",
      []
    );

    const filteredCollaboratorListFromExistingException = _.reduce(
      existingExceptionalCollaborators,
      (result, item) => {
        const staffid = item.node.id;
        const staffCourses = _.map(item.node.courses, "id");
        const isCoursePresent = _.intersection(allCourses, staffCourses);
        if (isCoursePresent.length === 0) {
          result.push(staffid);
        }

        return result;
      },
      []
    );

    // Removed teacher functionality - no need to filter collaborators
    const filteredRemovedExceptionList = [];

    const finalRemovedExceptionalCollaborators = [
      ...filteredRemovedExceptionList,
      ...filteredCollaboratorListFromExistingException,
    ];

    // Removed teacher functionality - no need to filter collaborators
    const finalAddedExceptionalCollaborators = [];

    const courseId = getSelectedCourseId(state);

    const refetchQueries = [
      {
        query: getWeeklyPlannerTemplateByCourseQuery,
        variables: {
          id: courseId,
        },
      },
    ];

    try {
      await client.mutate({
        mutation: updateWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            courses,
            defaultAccessState,
            exceptionalCollaborators: {
              addedExceptionalCollaborators: finalAddedExceptionalCollaborators,
              removedExceptionalCollaborators: finalRemovedExceptionalCollaborators,
            },
            enabledSections,
            isSharedWithFamilies,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_shared",
          type: "success",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const createWeeklyPlannerSection = ({
  classPlannerId,
  startWeekDate,
  endWeekDate,
  showSuccessToast = true,
  sectionDetails = {},
}) => async (dispatch, getState) => {
  const {
    title,
    startDate,
    endDate,
    colorCode,
    startTime,
    endTime,
    recurrenceSetting,
    timezone,
    duration,
    // classId,
  } = sectionDetails;
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: createWeeklyPlannerSectionMutation,
      variables: {
        input: {
          title,
          templateId: classPlannerId,
          startDate,
          endDate,
          startTime,
          endTime,
          colorCode,
          timezone,
          recurrenceSetting,
          duration,
          // classId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else if (showSuccessToast) {
    dispatch(
      setToastMsg({
        type: "success",
        msg: "toastMsgs:successfully_with_label",
        locale_params: [
          { key: "label", value: "toastMsgs:added", isPlainText: false },
        ],
      })
    );
  }
  return { error };
};

export const updateWeeklyPlannerSection = ({
  sectionDetails,
  actionFor,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async dispatch => {
  const {
    id,
    title,
    startDate,
    endDate,
    colorCode,
    startTime,
    endTime,
    recurrenceSetting,
    timezone,
    duration,
    // classId,
  } = sectionDetails;

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionMutation,
      variables: {
        input: {
          id,
          title,
          startDate,
          endDate,
          startTime,
          endTime,
          colorCode,
          actionFor,
          timezone,
          recurrenceSetting,
          duration,
          // classId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:block_updated_successfully",
        type: "success",
      })
    );
  }
  return { error };
};

export const deleteWeeklyPlannerSection = ({
  sectionId,
  classPlannerId,
  startWeekDate,
  endWeekDate,
  actionFor,
}) => async dispatch => {
  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: deleteWeeklyPlannerSectionMutation,
      variables: {
        input: {
          id: sectionId,
          actionFor,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:block_deleted_successfully",
        type: "success",
      })
    );
  }
  return { error };
};

export const updateWeeklyPlannerSectionItems = ({
  sectionId,
  editItems,
  isDeleted,
  description,
}) => async dispatch => {
  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionItemsMutation,
      variables: {
        input: {
          id: sectionId,
          description,
          editItems,
        },
      },
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: isDeleted
          ? "toastMsgs:item_successfully_removed_from"
          : "toastMsgs:item_successfully_added_to",
        locale_params: [
          { key: "fromItem", value: "toastMsgs:section", isPlainText: false },
          {
            key: "destination",
            value: "toastMsgs:section",
            isPlainText: false,
          },
        ],
        type: "success",
      })
    );
  }
  return {
    error,
  };
};

export const updateWeeklyPlannerSectionItemDescription = ({
  sectionId,
  description,
}) => async dispatch => {
  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionDescriptionMutation,
      variables: {
        input: {
          id: sectionId,
          description,
        },
      },
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const reorderWeeklyPlannerSectionContent = ({
  classPlannerId,
  startWeekDate,
  endWeekDate,
  plannerSectionId,
  putAfterItemId,
  id,
  draggedDestinationIndex,
  draggedSourceIndex,
  sectionId,
  itemFilterId,
}) => async dispatch => {
  const sourceData = readWeeklyPlannerTemplateSectionFragment({
    id: sectionId,
    variables: {},
  });

  const filteredSectionItem = _.find(sourceData.items, item => {
    return _.get(item, "sectionItem.id") === itemFilterId;
  });

  const updatedSourceData = update(sourceData, {
    items: {
      $splice: [
        [draggedSourceIndex, 1],
        [draggedDestinationIndex, 0, filteredSectionItem],
      ],
    },
  });

  writeWeeklyPlannerTemplateSectionFragmentToCache({
    id: sectionId,
    data: updatedSourceData,
    variables: {},
  });

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: reorderWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          plannerSectionId,
          putAfterItemId,
          id,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:item_reordered_successfully",
        type: "success",
      })
    );
  }
  return {
    error,
  };
};

export const moveWeeklyPlannerSectionContent = ({
  sourceSectionId,
  destinationSectionId,
  itemFilter,
  classPlannerId,
  startWeekDate,
  endWeekDate,
  putAfterItemId,
  draggedDestinationIndex,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const isDragAndDropContent = !_.isEmpty(itemFilter);
  if (isDragAndDropContent) {
    const sourceData = readWeeklyPlannerTemplateSectionFragment({
      id: sourceSectionId,
      variables: { collaboratorFilters },
    });

    let filteredSectionItem;
    const filteredSectionItems = _.filter(sourceData.items, item => {
      if (_.get(item, "sectionItem.id") == itemFilter?.id) {
        filteredSectionItem = { ...item, isItemDragged: true };
        return false;
      }
      return true;
    });

    const updatedSourceData = update(sourceData, {
      items: { $set: filteredSectionItems },
    });

    writeWeeklyPlannerTemplateSectionFragmentToCache({
      id: sourceSectionId,
      data: updatedSourceData,
      variables: { collaboratorFilters },
    });

    const destinationData = readWeeklyPlannerTemplateSectionFragment({
      id: destinationSectionId,
      variables: {},
    });

    if (filteredSectionItem) {
      const updatedDestinationData = update(destinationData, {
        items: {
          $splice: [[draggedDestinationIndex, 0, filteredSectionItem]],
        },
      });
      writeWeeklyPlannerTemplateSectionFragmentToCache({
        id: destinationSectionId,
        data: updatedDestinationData,
        variables: {},
      });
    }
  }

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
      },
    },
  ];
  const [error] = await asyncWrap(
    client.mutate({
      mutation: moveWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          sourceSectionId,
          destinationSectionId,
          itemFilter,
          putAfterItemId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:items_moved_successfully",
        type: "success",
      })
    );
  }
  return {
    error,
  };
};

export const clearWeeklyPlannerSectionContent = ({
  sectionIds,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: clearWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          ids: sectionIds,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:items_cleared_successfully",
        type: "success",
      })
    );
  }
  return {
    error,
  };
};

export const duplicateWeeklyPlannerSectionContent = ({
  input,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: duplicateWeeklyPlannerSectionContentMutation,
      variables: {
        input,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:items_duplicated_successfully",
        type: "success",
      })
    );
  }
  return {
    error,
  };
};

export const getWeeklyPlannerDownloadUrl = params => {
  const {
    input: { folderName, isLongRunningTask },
  } = params;
  const fileId = generateRandomId();
  /*Have added if (!isLongRunningTask) on multiple instances below since getWeeklyPlannerDownloadUrl will behave in two different ways. 
  If it's a longRunnerTask then we are sending a link so we have to show a different toast message and 
  if it's not a longRunnerTask then we have to show the progress bar as well as download the pdf from the given URL.*/
  const attachment = {
    id: fileId,
    type: "FILE",
    name: folderName,
    mimeType: "application/zip",
  };
  return async dispatch => {
    if (!isLongRunningTask)
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );

    try {
      const result = await client.mutate({
        mutation: getWeeklyPlannerDownloadUrlMutation,
        variables: params,
      });
      const { isSuccess, url, longRunningTaskCreated } = _.get(
        result,
        "data.planner.weeklyPlannerTemplateDownloadUrl"
      );
      if (!isLongRunningTask) dispatch(removeProgressElement({ id: fileId }));
      if (isSuccess) {
        if (!isLongRunningTask)
          dispatch(
            downloadFromUrl({
              attachment: {
                name: folderName,
                url,
                mimeType: "application/zip",
              },
              shouldAskForSave: true,
            })
          );
        else if (longRunningTaskCreated)
          dispatch(
            setToastMsg({
              msg: "toastMsgs:class_planner_download_success_msg",
              type: "success",
            })
          );
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } catch (error) {
      if (!isLongRunningTask) dispatch(removeProgressElement({ id: fileId }));
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const importResourcesToSection = ({
  sectionId,
  selectedItems,
  classPlannerId,
  itemType,
  isNoteCreation = false,
}) => async (dispatch, getState) => {
  const dateRange = _.get(getState(), "weeklyPlanning.dateRange");
  const startWeekDate = _.first(dateRange);
  const endWeekDate = _.last(dateRange);

  // Transform selected items into the format expected by the mutation
  // Use the provided itemType consistently for all items
  const resourceItems = _.reduce(
    selectedItems,
    (result, item) => {
      if (item?.id) {
        result.push({
          id: item.id,
          itemType: itemType,
        });
      }
      return result;
    },
    []
  );

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
      },
    },
  ];

  const [error, result] = await asyncWrap(
    client.mutate({
      mutation: importResourcesToSectionMutation,
      variables: {
        sectionId,
        resourceItems,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    return { error };
  }

  const updatedSection = _.get(
    result,
    "data.planner.updateWeeklyPlannerSection"
  );

  if (updatedSection) {
    let msg = "toastMsgs:successfully_with_label",
      locale_params = [
        { key: "label", value: "toastMsgs:imported", isPlainText: false },
      ];

    if (isNoteCreation) {
      msg = "toastMsgs:description_added_successfully";
      locale_params = [
        { key: "action", value: "common:created", isPlainText: false },
      ];
    }

    dispatch(
      setToastMsg({
        type: "success",
        msg,
        locale_params,
      })
    );
  }

  return { error: false, updatedSection };
};

const REDUCER_HANDLERS = {
  [UPDATE_CONFIGURATION_FILTER]: (state, action) => {
    return update(state, { configurationFilters: { $set: action.data } });
  },

  [INITIALIZE_CONFIGURATION_FILTER]: (state, action) => {
    return update(state, {
      configurationFilters: { $set: initialState.configurationFilters },
      assignmentModalData: { $set: null },
    });
  },

  [SET_ASSIGNMENT_MODAL_DATA]: (state, action) => {
    return update(state, {
      assignmentModalData: { $set: action.data },
    });
  },
};

const initialState = {
  configurationFilters: [],
  assignmentModalData: null,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
